import * as THREE from "three";
import type { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { Pano360Manifest, Pano360Node } from "../types.ts";
import { EquirectSource } from "./EquirectSource.ts";
import { PanoramaSession } from "./PanoramaSession.ts";

const MARKER_RADIUS = 0.05;
const DRAG_THRESHOLD_PX = 5;
const MARKER_COLOR = 0xffaa4a;            // orange — distinct from blue pinhole markers
const MARKER_COLOR_HOVER = 0xffc46a;
const MARKER_COLOR_ALIGNED = 0x4aff6a;    // green for aligned nodes

/**
 * 360° Insta360 panorama layer: octahedron markers on the floor plan, click to
 * enter panorama mode. Panorama rendering goes through the shared
 * {@link PanoramaSession} backed by {@link EquirectSource}.
 *
 * Independent of the RTABmap-pinhole {@link import('./StreetView.ts').StreetView}
 * — both can be loaded for the same project, but only one set of markers is
 * shown at a time.
 */
export class Pano360View {
  private manifest: Pano360Manifest | null = null;
  private source: EquirectSource | null = null;
  private session: PanoramaSession | null = null;

  private markersGroup: THREE.Group;
  private markerMeshes = new Map<number, THREE.Mesh>();
  private sharedGeometry: THREE.OctahedronGeometry;
  private sharedMaterial: THREE.MeshBasicMaterial;
  private alignedMaterial: THREE.MeshBasicMaterial;
  private hoverMaterial: THREE.MeshBasicMaterial;

  // Heading wedges (one per node, parented to floor plan, rotates with node yaw).
  private wedgesGroup: THREE.Group;
  private wedgeGeometry: THREE.ShapeGeometry;
  private wedgeMaterial: THREE.MeshBasicMaterial;
  private wedgeMaterialAligned: THREE.MeshBasicMaterial;
  private wedgeGroups = new Map<number, THREE.Group>();

  // Text labels (Sprite per node showing #id · filename).
  private labelsGroup: THREE.Group;
  private labelSprites = new Map<number, THREE.Sprite>();

  private raycaster = new THREE.Raycaster();
  private hoveredMarker: THREE.Mesh | null = null;
  private interactive = true;
  private _downX = 0;
  private _downY = 0;

  onPanoExit: (() => void) | null = null;
  /** Fires after `session.enter()` resolves — first texture is bound, overlay is open. */
  onPanoEnter: ((nodeId: number) => void) | null = null;

  constructor(
    private camera: THREE.PerspectiveCamera,
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private controls: OrbitControls,
    private planRoot: THREE.Group,
  ) {
    this.sharedGeometry = new THREE.OctahedronGeometry(MARKER_RADIUS);
    this.sharedMaterial = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR, transparent: true, opacity: 0.9, depthTest: false,
    });
    this.alignedMaterial = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR_ALIGNED, transparent: true, opacity: 0.95, depthTest: false,
    });
    this.hoverMaterial = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR_HOVER, transparent: true, opacity: 1, depthTest: false,
    });

    // Wedge: triangle in shape XY, tip at +Y (becomes +Z after laying flat with rotation.x = π/2).
    const W = 0.18, L = 0.42;
    const wedgeShape = new THREE.Shape();
    wedgeShape.moveTo(0, L);
    wedgeShape.lineTo(-W, 0);
    wedgeShape.lineTo(W, 0);
    wedgeShape.lineTo(0, L);
    this.wedgeGeometry = new THREE.ShapeGeometry(wedgeShape);
    this.wedgeMaterial = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR, transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthTest: false,
    });
    this.wedgeMaterialAligned = new THREE.MeshBasicMaterial({
      color: MARKER_COLOR_ALIGNED, transparent: true, opacity: 0.65, side: THREE.DoubleSide, depthTest: false,
    });

    this.markersGroup = new THREE.Group();
    this.markersGroup.renderOrder = 999;
    this.markersGroup.visible = false;
    this.planRoot.add(this.markersGroup);

    this.wedgesGroup = new THREE.Group();
    this.wedgesGroup.renderOrder = 998;
    this.wedgesGroup.visible = false;
    this.planRoot.add(this.wedgesGroup);

    this.labelsGroup = new THREE.Group();
    this.labelsGroup.visible = false;
    this.planRoot.add(this.labelsGroup);

    const canvas = this.renderer.domElement;
    canvas.addEventListener("pointerdown", this._onPointerDown);
    canvas.addEventListener("mousemove", this._onMouseMove);
    canvas.addEventListener("click", this._onClick, true);
  }

  get loaded(): boolean { return this.manifest !== null; }
  get isPanoActive(): boolean { return this.session?.isActive ?? false; }
  get markersVisible(): boolean { return this.markersGroup.visible; }

  async loadManifest(manifestUrl: string, imageBaseUrl: string, modelOffset: THREE.Vector3): Promise<void> {
    const resp = await fetch(manifestUrl);
    if (!resp.ok) throw new Error(`Failed to load pano360 manifest (HTTP ${resp.status})`);
    this.manifest = await resp.json();
    this.source = new EquirectSource(this.manifest!, imageBaseUrl, modelOffset);
    this.session = new PanoramaSession(
      this.camera, this.renderer, this.scene, this.controls, this.planRoot, this.source,
    );
    this.session.onExit = () => this.onPanoExit?.();

    this._createMarkers();
  }

  setMarkersVisible(v: boolean): void {
    this.markersGroup.visible = v;
    this.wedgesGroup.visible = v;
    this.labelsGroup.visible = v;
  }

  /** Open panorama at the closest node to the current orbit camera. */
  async enterPanorama(): Promise<void> {
    if (!this.session) return;
    if (this.session.isActive) return;
    const nodeId = this.session.findNearestToCamera();
    if (nodeId < 0) return;
    await this.session.enter(nodeId);
    this.onPanoEnter?.(nodeId);
  }

  /** Open panorama at a specific node id. */
  async enterPanoramaAtNode(nodeId: number): Promise<void> {
    if (!this.session) return;
    if (this.session.isActive) return;
    await this.session.enter(nodeId);
    this.onPanoEnter?.(nodeId);
  }

  /** When false, marker clicks/hovers are ignored — for hand-off to alignment editor. */
  setInteractive(on: boolean): void {
    this.interactive = on;
  }

  exitPanorama(): void {
    this.session?.exit();
  }

  /** Live-update active panorama sphere yaw + active node's heading wedge. */
  setLiveSphereYaw(yaw: number): void {
    this.session?.setLiveSphereYaw(yaw);
    if (this.session?.isActive) {
      const id = this.session.nodeId;
      const wedge = this.wedgeGroups.get(id);
      if (wedge) wedge.rotation.y = yaw;
    }
  }

  /** ID of the currently-displayed panorama node, or -1 if none. */
  get activeNodeId(): number {
    return this.session?.nodeId ?? -1;
  }

  /** Snapshot of a node's current data (after any in-memory edits). */
  getNodeData(id: number): Pano360Node | null {
    return this.manifest?.nodes.find((n) => n.id === id) ?? null;
  }

  /** Raycast pano360 markers at NDC coords; returns hit node id or -1. */
  pickMarker(ndc: THREE.Vector2, raycaster: THREE.Raycaster): number {
    raycaster.setFromCamera(ndc, this.camera);
    const hits = raycaster.intersectObjects(this.markersGroup.children, false);
    if (!hits.length) return -1;
    return (hits[0].object.userData.pano360NodeId as number | undefined) ?? -1;
  }

  /**
   * Apply an in-memory edit to a node: marker position + alignment colour, the
   * source's positions/yaws maps, and (if the pano session is showing this
   * node) the live sphere rotation.
   *
   * Caller is responsible for persisting the change via the backend PATCH.
   */
  applyNodeUpdate(
    id: number,
    patch: { position?: THREE.Vector3; yaw?: number; aligned?: boolean },
  ): void {
    if (!this.manifest || !this.source) return;
    const node = this.manifest.nodes.find((n) => n.id === id);
    if (!node) return;

    if (patch.position) {
      node.position = { x: patch.position.x, y: patch.position.y, z: patch.position.z };
      this.source.updateNode(id, { position: patch.position });
      const marker = this.markerMeshes.get(id);
      if (marker) marker.position.copy(patch.position);
      const wedge = this.wedgeGroups.get(id);
      if (wedge) {
        wedge.position.copy(patch.position);
        wedge.position.y -= 0.04;
      }
      const sprite = this.labelSprites.get(id);
      if (sprite) {
        sprite.position.copy(patch.position);
        sprite.position.y += 0.32;
      }
    }
    if (typeof patch.yaw === "number") {
      node.yaw = patch.yaw;
      this.source.updateNode(id, { yaw: patch.yaw });
      if (this.activeNodeId === id) this.setLiveSphereYaw(patch.yaw);
      const wedge = this.wedgeGroups.get(id);
      if (wedge) wedge.rotation.y = patch.yaw;
    }
    if (typeof patch.aligned === "boolean") {
      node.aligned = patch.aligned;
      const marker = this.markerMeshes.get(id);
      if (marker) {
        marker.material = patch.aligned ? this.alignedMaterial : this.sharedMaterial;
      }
      const wedge = this.wedgeGroups.get(id);
      if (wedge) {
        const child = wedge.children[0] as THREE.Mesh | undefined;
        if (child) child.material = patch.aligned ? this.wedgeMaterialAligned : this.wedgeMaterial;
      }
    }
  }

  dispose(): void {
    this.session?.dispose();
    this.source?.dispose();
    const c = this.renderer.domElement;
    c.removeEventListener("pointerdown", this._onPointerDown);
    c.removeEventListener("mousemove", this._onMouseMove);
    c.removeEventListener("click", this._onClick, true);
    this.markersGroup.removeFromParent();
    this.wedgesGroup.removeFromParent();
    this.labelsGroup.removeFromParent();
    this.sharedGeometry.dispose();
    this.sharedMaterial.dispose();
    this.alignedMaterial.dispose();
    this.hoverMaterial.dispose();
    this.wedgeGeometry.dispose();
    this.wedgeMaterial.dispose();
    this.wedgeMaterialAligned.dispose();
    for (const sprite of this.labelSprites.values()) {
      const m = sprite.material as THREE.SpriteMaterial;
      m.map?.dispose();
      m.dispose();
    }
    this.markerMeshes.clear();
    this.wedgeGroups.clear();
    this.labelSprites.clear();
  }

  // ── Markers ───────────────────────────────────────────────────────────────

  private _createMarkers(): void {
    if (!this.source || !this.manifest) return;
    for (const m of this.markerMeshes.values()) m.removeFromParent();
    for (const g of this.wedgeGroups.values()) g.removeFromParent();
    for (const s of this.labelSprites.values()) {
      s.removeFromParent();
      const m = s.material as THREE.SpriteMaterial;
      m.map?.dispose();
      m.dispose();
    }
    this.markerMeshes.clear();
    this.wedgeGroups.clear();
    this.labelSprites.clear();

    const nodeById = new Map<number, Pano360Node>();
    for (const n of this.manifest.nodes) nodeById.set(n.id, n);

    for (const node of this.source.getNodes()) {
      const data = nodeById.get(node.id);
      const aligned = (data?.aligned) !== false;

      // Marker (octahedron, clickable).
      const mesh = new THREE.Mesh(
        this.sharedGeometry,
        aligned ? this.alignedMaterial : this.sharedMaterial,
      );
      mesh.position.copy(node.position);
      mesh.userData.pano360NodeId = node.id;
      mesh.renderOrder = 999;
      this.markersGroup.add(mesh);
      this.markerMeshes.set(node.id, mesh);

      // Heading wedge (flat triangle on the floor plane). Group rotates around
      // world Y by node.yaw; child mesh lies flat with rotation.x = π/2 so the
      // shape tip becomes local +Z. Net: tip points toward (sin yaw, _, cos yaw).
      const wedgeMesh = new THREE.Mesh(
        this.wedgeGeometry,
        aligned ? this.wedgeMaterialAligned : this.wedgeMaterial,
      );
      wedgeMesh.rotation.x = Math.PI / 2;
      const wedgeGrp = new THREE.Group();
      wedgeGrp.add(wedgeMesh);
      wedgeGrp.position.copy(node.position);
      wedgeGrp.position.y -= 0.04;  // sit just below the marker octahedron
      wedgeGrp.rotation.y = data?.yaw ?? 0;
      wedgeGrp.renderOrder = 998;
      this.wedgesGroup.add(wedgeGrp);
      this.wedgeGroups.set(node.id, wedgeGrp);

      // Label sprite: short ID + filename (truncated). Floats above the marker.
      const file = data?.image ?? "";
      const shortFile = file.length > 14 ? `…${file.slice(-13)}` : file;
      const sprite = this._buildLabelSprite(`#${node.id} · ${shortFile}`);
      sprite.position.copy(node.position);
      sprite.position.y += 0.32;
      this.labelsGroup.add(sprite);
      this.labelSprites.set(node.id, sprite);
    }
  }

  private _buildLabelSprite(text: string): THREE.Sprite {
    const c = document.createElement("canvas");
    c.width = 384; c.height = 72;
    const ctx = c.getContext("2d")!;
    // Pill background with subtle accent border.
    const r = 14;
    ctx.beginPath();
    ctx.moveTo(r + 1, 1);
    ctx.arcTo(c.width - 1, 1, c.width - 1, c.height - 1, r);
    ctx.arcTo(c.width - 1, c.height - 1, 1, c.height - 1, r);
    ctx.arcTo(1, c.height - 1, 1, 1, r);
    ctx.arcTo(1, 1, c.width - 1, 1, r);
    ctx.closePath();
    ctx.fillStyle = "rgba(15, 15, 18, 0.86)";
    ctx.fill();
    ctx.strokeStyle = "rgba(255, 170, 74, 0.55)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#ffd9aa";
    ctx.font = "600 30px system-ui, -apple-system, 'Segoe UI', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, c.width / 2, c.height / 2);

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.minFilter = THREE.LinearFilter;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false });
    const s = new THREE.Sprite(mat);
    s.scale.set(0.85, 0.16, 1);
    s.renderOrder = 1000;
    return s;
  }

  private _ndcFromEvent(e: MouseEvent): THREE.Vector2 {
    const r = this.renderer.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -((e.clientY - r.top) / r.height) * 2 + 1,
    );
  }

  private _restoreMarker(m: THREE.Mesh): void {
    const aligned = (this.manifest?.nodes.find(n => n.id === m.userData.pano360NodeId)?.aligned) !== false;
    m.material = aligned ? this.alignedMaterial : this.sharedMaterial;
    m.scale.setScalar(1);
  }

  private _onPointerDown = (e: PointerEvent): void => { this._downX = e.clientX; this._downY = e.clientY; };

  private _onMouseMove = (e: MouseEvent): void => {
    if (!this.interactive || !this.markersGroup.visible || this.session?.isActive) return;
    const mouse = this._ndcFromEvent(e);
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.markersGroup.children, false);

    if (this.hoveredMarker) this._restoreMarker(this.hoveredMarker);
    this.hoveredMarker = null;

    if (hits.length > 0) {
      const m = hits[0].object as THREE.Mesh;
      m.material = this.hoverMaterial;
      m.scale.setScalar(1.4);
      this.hoveredMarker = m;
      this.renderer.domElement.style.cursor = "pointer";
    } else {
      this.renderer.domElement.style.cursor = "";
    }
  };

  private _onClick = (e: MouseEvent): void => {
    if (!this.interactive || !this.markersGroup.visible || this.session?.isActive || !this.session) return;
    if (Math.hypot(e.clientX - this._downX, e.clientY - this._downY) >= DRAG_THRESHOLD_PX) return;
    const mouse = this._ndcFromEvent(e);
    this.raycaster.setFromCamera(mouse, this.camera);
    const hits = this.raycaster.intersectObjects(this.markersGroup.children, false);
    if (hits.length === 0) return;
    const nodeId = hits[0].object.userData.pano360NodeId as number | undefined;
    if (nodeId === undefined) return;
    e.stopImmediatePropagation();
    const session = this.session;
    void (async () => { await session.enter(nodeId); this.onPanoEnter?.(nodeId); })();
  };
}
